import { useEffect, useRef, useState } from "react";
import { Typography, Result } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";

import { ContentWrapper } from "../../common/styledComponents";
import CustomerInfoBanner from "../../common/CustomerInfoBanner";
import { KontaxUserContext } from "../../contexts/KontaxUserContext";
import UserFilters from "../Mandanten/Common/UserFilters";
import { useFilteredUser } from "@/lib/hooks/useFilteredUser";
import { ReceiptTable } from "./ReceiptTable";
import type { ReceiptFilterValues } from "./ReceiptTable";
import { FilterPanel } from "./FilterPanel";
import { normalizeLegacySourceFilters } from "./sourceFilterOptions";
import { useReceiptSourceFacetsQuery } from "../../../api/graphql/queries/receiptSourceFacets/receiptSourceFacets.generated";
import {
  DocumentUploadSource,
  MatchingStatusFilter,
} from "../../../api/graphql/schema.generated";
import ErrorBoundary from "../../common/ErrorBoundary";
import { getBerlinMomentTimezone } from "../../common/helpers";
import { SAVE_DATE_FORMAT } from "@/v1/constants";

const { Text } = Typography;

// SMT-1127: search + filters on one row. UserFilters caps its search input at
// xl={8} (33% of its row), which left a big empty gap between the search and
// the filters. SearchSlot gives the search a fixed, longer width and lets its
// single column fill the slot; FilterSlot places the filters right after it.
const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const SearchSlot = styled.div`
  flex: 0 1 460px;
  min-width: 260px;

  .ant-row > .ant-col {
    max-width: 100%;
    flex: 1 1 auto;
  }
`;

const FilterSlot = styled.div`
  flex: 1 1 auto;
`;

/**
 * Returns the current-year date range {dateFrom, dateTo} using the Berlin
 * timezone so the range is consistent with the rest of the app.
 * SMT-994: used as the default filter on fresh load and after reset.
 * SMT-1306: the range is applied to the created-at pair, not the invoice pair.
 */
const getCurrentYearDateRange = (): { dateFrom: string; dateTo: string } => {
  const berlin = getBerlinMomentTimezone();
  return {
    dateFrom: berlin.clone().startOf("year").format(SAVE_DATE_FORMAT),
    dateTo: berlin.clone().endOf("year").format(SAVE_DATE_FORMAT),
  };
};

/** URL param schema for the 7 receipt filter values. */
const FILTER_PARAMS = {
  dateFrom: StringParam,
  dateTo: StringParam,
  uploadDateFrom: StringParam,
  uploadDateTo: StringParam,
  uploadSources: ArrayParam,
  documentOrigins: ArrayParam,
  matchingStatus: StringParam,
} as const;

const ReceiptOverviewView = () => {
  const { t } = useTranslation("ReceiptOverview");
  const { user, filterOptions, setFilterOptions } = useFilteredUser();

  // SMT-1238: the sources this customer actually has receipts from, so the
  // FilterPanel dropdown can offer only those instead of the full static
  // provider list. `skip` while no customer is selected — there is nothing to
  // ask the facets for and no FilterPanel rendered yet either.
  const {
    data: sourceFacetsData,
    loading: sourceFacetsLoading,
    error: sourceFacetsError,
    refetch: refetchSourceFacets,
  } = useReceiptSourceFacetsQuery({
    variables: { publicId: user?.publicId ?? "" },
    skip: !user,
    // Without this, Apollo keeps `loading` false during refetch() (after an
    // upload), and the dropdown would stay enabled on options that are about
    // to be replaced. With it, `loading` covers the initial load, a customer
    // switch, and the refetch alike.
    notifyOnNetworkStatusChange: true,
  });

  // URL-synced filter state. useQueryParams reads from (and writes to) the
  // current URL, so filters survive page reloads and can be shared via link.
  const [rawParams, setParams] = useQueryParams(FILTER_PARAMS);

  // SMT-994: once the operator empties the date selection on purpose - clearing
  // the year/range, or switching the date field without picking a new value yet
  // - we must stop snapping back to the current-year default and let the
  // "all dates" state stand. This is session state by design: a fresh page load
  // always starts from the current-year default again.
  const [datesExplicitlyCleared, setDatesExplicitlyCleared] = useState(false);

  // SMT-997: bumped after an upload to remount the FilterPanel so its date-field
  // selector re-derives to "Erstellt am" from the freshly-set upload-date range.
  const [filterPanelNonce, setFilterPanelNonce] = useState(0);

  // SMT-994: when the URL has no date filter at all (fresh load / after a
  // customer-switch clear) and the operator has not explicitly cleared the
  // dates, default to the current year so the table is not overwhelmed with
  // all-time data. SMT-1306: that default range goes on the created-at pair, so
  // receipts whose invoice date OCR could not extract are visible too.
  const hasUrlDateFilter =
    rawParams.dateFrom != null ||
    rawParams.dateTo != null ||
    rawParams.uploadDateFrom != null ||
    rawParams.uploadDateTo != null;

  const yearDefault =
    hasUrlDateFilter || datesExplicitlyCleared
      ? undefined
      : getCurrentYearDateRange();

  // Coerce ArrayParam's (string | null)[] | null | undefined to
  // DocumentUploadSource[] / string[] | undefined.
  const rawUploadSources =
    rawParams.uploadSources && rawParams.uploadSources.length > 0
      ? (rawParams.uploadSources.filter(Boolean) as DocumentUploadSource[])
      : undefined;
  const rawDocumentOrigins =
    rawParams.documentOrigins && rawParams.documentOrigins.length > 0
      ? (rawParams.documentOrigins.filter(Boolean) as string[])
      : undefined;

  // SMT-1238 reviewer finding: a legacy `?uploadSources=SMARTA_DOCUMENTS` link
  // (the pre-SMT-1191 shape of the "Integration" filter) still filters the
  // backend query, but the dropdown has not produced that value since
  // SMT-1191 and would show "All sources" with nothing ticked — an active
  // filter invisible to the operator. Normalising here (rather than only in
  // the URL-rewrite effect below) means the very first receipts query and the
  // very first FilterPanel render already use the equivalent provider-origin
  // selection — there is no render in between with the invisible filter.
  const normalizedSourceFilters = normalizeLegacySourceFilters(
    rawUploadSources,
    rawDocumentOrigins
  );

  const filters: ReceiptFilterValues = {
    dateFrom: rawParams.dateFrom ?? undefined,
    dateTo: rawParams.dateTo ?? undefined,
    uploadDateFrom: rawParams.uploadDateFrom ?? yearDefault?.dateFrom,
    uploadDateTo: rawParams.uploadDateTo ?? yearDefault?.dateTo,
    uploadSources: normalizedSourceFilters.uploadSources,
    documentOrigins: normalizedSourceFilters.documentOrigins,
    // Only accept a known enum value from the URL so a hand-edited param can't
    // send an invalid matchingStatus that the backend would reject.
    matchingStatus: (Object.values(MatchingStatusFilter) as string[]).includes(
      rawParams.matchingStatus ?? ""
    )
      ? (rawParams.matchingStatus as MatchingStatusFilter)
      : undefined,
  };

  // SMT-1238 reviewer finding: rewrite the URL once the legacy value has been
  // normalised, so the address bar (and any link re-shared from it) carries
  // the equivalent provider-origin selection instead of the legacy value.
  // Deps are the joined primitive strings (as elsewhere in this file) rather
  // than the arrays themselves, which `normalizeLegacySourceFilters` returns
  // as fresh literals on every render. This cannot loop: once the rewrite
  // lands, the URL no longer carries `SMARTA_DOCUMENTS`, so on the next
  // render `normalizedSourceFilters.changed` is false and the effect is a
  // no-op.
  const normalizedUploadSourcesKey = (
    normalizedSourceFilters.uploadSources ?? []
  ).join("|");
  const normalizedDocumentOriginsKey = (
    normalizedSourceFilters.documentOrigins ?? []
  ).join("|");
  useEffect(() => {
    if (!normalizedSourceFilters.changed) return;
    setParams(
      {
        uploadSources: normalizedSourceFilters.uploadSources,
        documentOrigins: normalizedSourceFilters.documentOrigins,
      },
      "replaceIn"
    );
    // setParams is left out, matching the customer-switch effect below, which
    // omits it for the same reason (it is not memoised against these deps and
    // including it would not add any information — only these three values
    // decide whether/what to write).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    normalizedSourceFilters.changed,
    normalizedUploadSourcesKey,
    normalizedDocumentOriginsKey,
  ]);

  // Track whether we have seen at least one publicId so we can skip the
  // initial-mount fire of the effect (URL filters should be preserved on
  // first load; only a genuine customer *change* should clear them).
  const prevPublicIdRef = useRef<string | undefined>(undefined);

  // OQ-2: reset URL filter params whenever the selected customer changes so
  // stale date ranges don't silently hide that customer's data.
  useEffect(() => {
    const prev = prevPublicIdRef.current;
    prevPublicIdRef.current = user?.publicId;

    // Skip the first mount (no previous customer) — preserve URL filters.
    if (prev === undefined) return;

    // A real customer change: clear all filter params from the URL and drop the
    // "explicitly cleared" state so the new customer starts from the current-year
    // default. Date params are cleared (not reset to current year) because the
    // URL will then have no date params, causing the current-year default to
    // apply via the filters derivation above (yearDefault) — no explicit seeding
    // needed.
    setDatesExplicitlyCleared(false);
    setParams(
      {
        dateFrom: undefined,
        dateTo: undefined,
        uploadDateFrom: undefined,
        uploadDateTo: undefined,
        uploadSources: undefined,
        documentOrigins: undefined,
        matchingStatus: undefined,
      },
      "replaceIn"
    );
  }, [user?.publicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (next: ReceiptFilterValues) => {
    // SMT-994: track whether the operator still has a date selection. When all
    // date pairs come back empty (the year/range was cleared, or the date field
    // was switched without a new pick) we record it so the current-year default
    // is not silently re-applied on the next render.
    const hasDateSelection = Boolean(
      next.dateFrom || next.dateTo || next.uploadDateFrom || next.uploadDateTo
    );
    setDatesExplicitlyCleared(!hasDateSelection);

    // Normalise empty arrays to undefined so the URL stays clean (no
    // ?uploadSources= with an empty value).
    setParams(
      {
        dateFrom: next.dateFrom ?? undefined,
        dateTo: next.dateTo ?? undefined,
        uploadDateFrom: next.uploadDateFrom ?? undefined,
        uploadDateTo: next.uploadDateTo ?? undefined,
        uploadSources:
          next.uploadSources && next.uploadSources.length > 0
            ? next.uploadSources
            : undefined,
        documentOrigins:
          next.documentOrigins && next.documentOrigins.length > 0
            ? next.documentOrigins
            : undefined,
        matchingStatus: next.matchingStatus ?? undefined,
      },
      "replaceIn"
    );
  };

  const handleReset = () => {
    // SMT-994: reset returns to the current-year range (not empty) so operators
    // always have a sensible default scope after clearing filters.
    // SMT-1306: that range is the created-at one, matching the fresh-load
    // default. FilterPanel resets its selector to "Erstellt am" in the same
    // step, so the dropdown and the query can never disagree.
    setDatesExplicitlyCleared(false);
    const { dateFrom, dateTo } = getCurrentYearDateRange();
    setParams(
      {
        dateFrom: undefined,
        dateTo: undefined,
        uploadDateFrom: dateFrom,
        uploadDateTo: dateTo,
        uploadSources: undefined,
        documentOrigins: undefined,
        matchingStatus: undefined,
      },
      "replaceIn"
    );
  };

  // SMT-997: after uploading a receipt from this view, put the date filter on
  // "Erstellt am" for the current year. A just-uploaded receipt has no invoice
  // date yet, so an invoice-date filter would hide it and ops could read that as
  // a failed upload. Clearing the source/matching filters too makes sure the new
  // (BACKOFFICE-source, unmatched) receipt is actually visible.
  // SMT-1306: since Erstellt am is now the default, this is a no-op for an
  // operator who never left it, and still a switch back for one who did.
  const handleReceiptUploaded = () => {
    setDatesExplicitlyCleared(false);
    const { dateFrom, dateTo } = getCurrentYearDateRange();
    setParams(
      {
        dateFrom: undefined,
        dateTo: undefined,
        uploadDateFrom: dateFrom,
        uploadDateTo: dateTo,
        uploadSources: undefined,
        documentOrigins: undefined,
        matchingStatus: undefined,
      },
      "replaceIn"
    );
    // SMT-1238: a fresh BACKOFFICE upload must make "TaxOps" appear in the
    // source dropdown immediately, not only after the next customer switch.
    // Called before bumping the nonce below: the panel remount briefly shows
    // the pre-refetch facets either way (same accepted staleness as after a
    // delete), so the ordering only matters for not losing the refetch itself.
    refetchSourceFacets();
    setFilterPanelNonce((n) => n + 1);
  };

  return (
    <ContentWrapper>
      {/* SMT-1127: search + filters on one row (oriented on VerificationView)
          to save vertical space. The filter block only appears once a customer
          is selected. */}
      <FilterBar>
        <SearchSlot>
          <Text strong>{t("filter.customerLabel")}</Text>
          <div style={{ marginTop: 4 }}>
            <UserFilters
              withoutLoadAll
              filterOptions={filterOptions}
              setFilterOptions={setFilterOptions}
            />
          </div>
        </SearchSlot>
        {user && (
          <FilterSlot>
            <FilterPanel
              key={filterPanelNonce}
              filters={filters}
              onChange={handleFilterChange}
              onReset={handleReset}
              // SMT-1238: a facet-query error must never make receipts
              // unfilterable, so we fall back to the static list (today's
              // behaviour) rather than surface the error here — the receipt
              // table itself still loads and reports its own errors.
              sourceFacets={
                sourceFacetsError
                  ? undefined
                  : sourceFacetsData?.receiptSourceFacets
              }
              sourceFacetsLoading={sourceFacetsLoading}
            />
          </FilterSlot>
        )}
      </FilterBar>
      <div className="mt-4">
        <div className="mt-2">
          {user ? (
            // KontaxUserContext feeds the shared customer-info banner (SMT-997,
            // reusing VerificationView's banner + "View more info" drawer; this
            // replaces the standalone SMT-1076 "More Info" panel).
            <KontaxUserContext email={user.email}>
              <CustomerInfoBanner />
              {/* key on ErrorBoundary remounts the whole subtree on customer
                  change, which resets both table state (page/sort/selection) and
                  any captured error state inside the boundary. */}
              <div className="mt-4">
                <ErrorBoundary
                  key={user.publicId}
                  fallback={
                    <Result
                      status="warning"
                      title={t("errorBoundary.title")}
                      subTitle={t("errorBoundary.message")}
                    />
                  }
                >
                  <ReceiptTable
                    publicId={user.publicId}
                    email={user.email}
                    filters={filters}
                    onReceiptUploaded={handleReceiptUploaded}
                    // SMT-1238 reviewer finding: a deleted receipt may have
                    // been the last one from its source, so refetch the
                    // facets on both delete paths too — otherwise the
                    // dropdown keeps offering a source with nothing left in
                    // it until the next reload.
                    onReceiptsDeleted={() => {
                      void refetchSourceFacets();
                    }}
                  />
                </ErrorBoundary>
              </div>
            </KontaxUserContext>
          ) : (
            <Text>{t("empty")}</Text>
          )}
        </div>
      </div>
    </ContentWrapper>
  );
};

export default ReceiptOverviewView;
